/* eslint-disable max-lines-per-function */
import type { Kitchen } from '@rta/rta-blueprint-library';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Flex,
	FormLabel,
	Image,
	Input,
	Text,
	Tooltip,
	useDisclosure
} from '@chakra-ui/react';
import { useCallback, useRef, useState, type JSX } from 'react';
import { Upload } from 'lucide-react';

import { lang } from '@lang';
import { useAppToast, useSetRendering } from '@hooks';

export type RenderingUploadZoneProps = {
	kitchen: Kitchen | null;
	hasExistingRendering: boolean;
};

const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const readAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = () => resolve(reader.result as string);
	reader.onerror = () => reject(new Error('readAsDataUrl: load failed'));
	reader.readAsDataURL(file);
});

export const RenderingUploadZone = (props: RenderingUploadZoneProps): JSX.Element => {
	const { kitchen, hasExistingRendering } = props;
	const appToast = useAppToast();
	const { setRendering, isSaving } = useSetRendering();
	const { isOpen, onOpen, onClose } = useDisclosure();

	const inputRef = useRef<HTMLInputElement>(null);
	const keepRef = useRef<HTMLButtonElement>(null);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	const { label, hint, setAsRendering, chooseDifferent, clickToSet, invalidType, confirm } = lang.contextPanels.renderingUpload;

	const handleFiles = useCallback(async (files: FileList | null): Promise<void> => {
		const file = files?.[0];
		if (!file) return;
		if (!ACCEPTED_TYPES.includes(file.type)) {
			appToast.error({ title: invalidType.title, description: invalidType.description });
			return;
		}
		setSelectedImage(await readAsDataUrl(file));
	}, [appToast, invalidType]);

	const handleDragOver = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(false);
		handleFiles(e.dataTransfer.files);
	}, [handleFiles]);

	const applyImage = useCallback(async (image: string): Promise<void> => {
		const success = await setRendering({ image, kitchen, source: 'raw' });
		if (success) setSelectedImage(null);
	}, [setRendering, kitchen]);

	const handleSetAsRendering = useCallback((): void => {
		if (!selectedImage) return;
		if (hasExistingRendering) onOpen();
		else applyImage(selectedImage);
	}, [selectedImage, hasExistingRendering, onOpen, applyImage]);

	const handleReplace = useCallback((): void => {
		if (!selectedImage) return;
		onClose();
		applyImage(selectedImage);
	}, [selectedImage, onClose, applyImage]);

	return (
		<Box borderTop="1px solid" borderColor="gray.200" pt={2}>
			<FormLabel fontSize="small">{label}</FormLabel>
			<Input ref={inputRef} type="file" accept="image/png,image/jpeg" display="none" onChange={(e) => handleFiles(e.target.files)} />

			{selectedImage ? (
				<Box>
					<Tooltip label={clickToSet} fontSize="xs">
						<Box
							borderRadius="md"
							overflow="hidden"
							border="2px solid"
							borderColor={isDragOver ? 'blue.400' : 'gray.200'}
							cursor="pointer"
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={handleSetAsRendering}
						>
							<Image src={selectedImage} alt="Uploaded rendering" objectFit="contain" maxH="140px" w="100%" bg="gray.50" />
						</Box>
					</Tooltip>
					<Flex mt={2} gap={2}>
						<Button size="sm" flex={1} isLoading={isSaving} isDisabled={isSaving} onClick={handleSetAsRendering}>
							{setAsRendering}
						</Button>
						<Button size="sm" variant="ghost" isDisabled={isSaving} onClick={() => inputRef.current?.click()}>
							{chooseDifferent}
						</Button>
					</Flex>
				</Box>
			) : (
				<Box
					border="2px dashed"
					borderColor={isDragOver ? 'blue.400' : 'gray.300'}
					borderRadius="md"
					p={4}
					textAlign="center"
					cursor="pointer"
					bg={isDragOver ? 'blue.50' : 'gray.50'}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onClick={() => inputRef.current?.click()}
				>
					<Flex direction="column" align="center" gap={1}>
						<Upload size={18} color="gray" />
						<Text fontSize="xs" color="gray.500">{hint}</Text>
					</Flex>
				</Box>
			)}

			{isOpen && (
				<AlertDialog isOpen leastDestructiveRef={keepRef} onClose={onClose}>
					<AlertDialogOverlay>
						<AlertDialogContent>
							<AlertDialogHeader fontSize="lg" fontWeight="bold">{confirm.title}</AlertDialogHeader>
							<AlertDialogBody>{confirm.body}</AlertDialogBody>
							<AlertDialogFooter>
								<Button ref={keepRef} onClick={onClose}>{confirm.keep}</Button>
								<Button colorScheme="red" ml={3} onClick={handleReplace}>{confirm.replace}</Button>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialogOverlay>
				</AlertDialog>
			)}
		</Box>
	);
};
